/**
 * @internal
 * Internal hook for TimePicker to get locale-based time format.
 */

import { useMemo } from 'react';
import { useLocaleContext } from '@localization/context/LocaleContext';
import { getLocaleTimeFormat } from './time.internal';
import type { ResolvedTimeFormat } from './time.types.internal';

/**
 * Hook that returns the appropriate time format (12h or 24h) based on the user's locale.
 *
 * Uses the locale from LocaleContext to detect whether the user's locale
 * typically uses 12-hour (AM/PM) or 24-hour time format.
 *
 * @returns ResolvedTimeFormat - '12h' for locales using AM/PM, '24h' for 24-hour locales
 *
 * @example
 * // In a component within LocaleProvider
 * const format = useLocaleTimeFormat();
 * // Returns '12h' for en-US, '24h' for de-DE, etc.
 *
 * @internal
 */
export function useLocaleTimeFormat(): ResolvedTimeFormat {
  const { language } = useLocaleContext();

  return useMemo(() => getLocaleTimeFormat(language), [language]);
}
